import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { X, Check, XCircle, Edit, Calendar, User, Building, MapPin, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const JobOfferDetailModal = ({ isOpen, onClose, onOfferUpdated, offer }) => {
  const [loading, setLoading] = useState(false);
  const [showAcceptFields, setShowAcceptFields] = useState(false);
  const [showJustificationModal, setShowJustificationModal] = useState(false);
  const [justification, setJustification] = useState('');
  const [acceptData, setAcceptData] = useState({
    taux_horaire: ''
  });

  if (!isOpen || !offer) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleAccept = () => {
    setShowAcceptFields(true);
  };

  const handleAcceptConfirm = async () => {
    if (!acceptData.taux_horaire || parseFloat(acceptData.taux_horaire) <= 0) {
      toast.error('Le taux horaire doit être un nombre positif');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers/${offer.id}/accept?taux_horaire=${acceptData.taux_horaire}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast.success('Offre acceptée avec succès');
        onOfferUpdated();
      } else {
        toast.error('Erreur lors de l\'acceptation de l\'offre');
      }
    } catch (error) {
      console.error('Error accepting offer:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir refuser cette offre ? Elle sera supprimée définitivement.')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers/${offer.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast.success('Offre refusée et supprimée');
        onOfferUpdated();
      } else {
        toast.error('Erreur lors du refus de l\'offre');
      }
    } catch (error) {
      console.error('Error rejecting offer:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleModify = async () => {
    // Show justification modal first
    setShowJustificationModal(true);
  };

  const handleModifyWithJustification = async () => {
    if (!justification.trim()) {
      toast.error('Une justification est requise pour renvoyer en modification');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Update offer status to "a_modifier" with justification
      const response = await fetch(`${API}/job-offers/${offer.id}/status?status=a_modifier`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          justification_modification: justification.trim()
        }),
      });

      if (response.ok) {
        toast.success('Offre renvoyée en modification avec justification');
        setShowJustificationModal(false);
        setJustification('');
        onOfferUpdated();
      } else {
        toast.error('Erreur lors du renvoi en modification');
      }
    } catch (error) {
      console.error('Error modifying offer:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowAcceptFields(false);
    setAcceptData({ taux_horaire: '' });
    onClose();
  };

  return (
    <div className="job-offer-detail-modal-overlay">
      <div className="job-offer-detail-modal">
        <Card className="job-offer-detail-modal-card">
          <CardHeader className="job-offer-detail-modal-header">
            <div className="flex justify-between items-center">
              <CardTitle className="job-offer-detail-modal-title">
                Détails de l'offre d'emploi
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="job-offer-detail-modal-close"
              >
                <X size={20} />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="job-offer-detail-modal-content">
            {/* Offer Information */}
            <div className="offer-details">
              <div className="offer-details-header">
                <h2 className="offer-title">{offer.company} : {offer.title}</h2>
              </div>

              <div className="offer-info-grid">
                <div className="offer-info-item">
                  <Building className="offer-info-icon" />
                  <span><strong>Entreprise :</strong> {offer.company}</span>
                </div>
                <div className="offer-info-item">
                  <User className="offer-info-icon" />
                  <span><strong>Manager :</strong> {offer.creator_name || 'Inconnu'}</span>
                </div>
                <div className="offer-info-item">
                  <Calendar className="offer-info-icon" />
                  <span><strong>Date idéale d'arrivée :</strong> {formatDate(offer.date_arrivee)}</span>
                </div>
                <div className="offer-info-item">
                  <MapPin className="offer-info-icon" />
                  <span><strong>Lieu de travail :</strong> {offer.lieu_travail || 'Non spécifié'}</span>
                </div>
                {offer.type_poste && (
                  <div className="offer-info-item">
                    <span><strong>Type de contrat :</strong> {offer.type_contrat}</span>
                  </div>
                )}
                {offer.taux_horaire && (
                  <div className="offer-info-item">
                    <DollarSign className="offer-info-icon" />
                    <span><strong>Taux horaire :</strong> {offer.taux_horaire}€</span>
                  </div>
                )}
              </div>

              <div className="offer-description">
                <h3>Description des missions</h3>
                <p>{offer.description}</p>
              </div>

              {offer.qualites_attendues && (
                <div className="offer-qualities">
                  <h3>Qualités attendues</h3>
                  <p>{offer.qualites_attendues}</p>
                </div>
              )}
            </div>

            {/* Accept Fields (shown when Accept is clicked) */}
            {showAcceptFields && (
              <div className="accept-fields">
                <h3>Informations supplémentaires requises</h3>
                <div className="accept-fields-form">
                  <div className="form-group">
                    <Label htmlFor="taux_horaire" className="form-label">
                      Taux horaire (en €) *
                    </Label>
                    <Input
                      id="taux_horaire"
                      type="number"
                      min="0"
                      step="0.01"
                      value={acceptData.taux_horaire}
                      onChange={(e) => setAcceptData(prev => ({ ...prev, taux_horaire: e.target.value }))}
                      className="form-input"
                      placeholder="Ex: 15.50"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="offer-detail-actions">
              {!showAcceptFields ? (
                <>
                  <Button
                    onClick={handleAccept}
                    disabled={loading}
                    className="btn-primary"
                    style={{ backgroundColor: '#22c55e', borderColor: '#22c55e' }}
                  >
                    <Check size={16} className="mr-2" />
                    Accepter
                  </Button>
                  <Button
                    onClick={handleReject}
                    disabled={loading}
                    className="btn-danger"
                    style={{ backgroundColor: '#ef4444', borderColor: '#ef4444' }}
                  >
                    <XCircle size={16} className="mr-2" />
                    Refuser
                  </Button>
                  <Button
                    onClick={handleModify}
                    disabled={loading}
                    variant="outline"
                    className="btn-outline"
                    style={{ borderColor: '#f59e0b', color: '#f59e0b' }}
                  >
                    <Edit size={16} className="mr-2" />
                    À modifier
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleAcceptConfirm}
                    disabled={loading}
                    className="btn-primary"
                    style={{ backgroundColor: '#22c55e', borderColor: '#22c55e' }}
                  >
                    {loading ? 'Validation...' : 'Valider l\'acceptation'}
                  </Button>
                  <Button
                    onClick={() => setShowAcceptFields(false)}
                    disabled={loading}
                    variant="outline"
                    className="btn-cancel"
                  >
                    Annuler
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Justification Modal */}
      {showJustificationModal && (
        <div className="justification-modal-overlay">
          <div className="justification-modal">
            <Card className="justification-modal-card">
              <CardHeader className="justification-modal-header">
                <div className="flex justify-between items-center">
                  <CardTitle className="justification-modal-title">
                    Justification de modification
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowJustificationModal(false)}
                    className="justification-modal-close"
                  >
                    <X size={20} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="justification-modal-content">
                <div className="form-group">
                  <Label htmlFor="justification" className="form-label">
                    Justification de la demande de modification *
                  </Label>
                  <Textarea
                    id="justification"
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                    className="form-textarea"
                    placeholder="Expliquez pourquoi cette offre doit être modifiée..."
                    rows={4}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Cette justification sera transmise au manager pour l'aider dans sa modification.
                  </p>
                </div>

                <div className="justification-actions">
                  <Button
                    onClick={handleModifyWithJustification}
                    disabled={loading || !justification.trim()}
                    className="btn-warning"
                    style={{ backgroundColor: '#f59e0b', borderColor: '#f59e0b' }}
                  >
                    <Edit size={16} className="mr-2" />
                    Renvoyer en modification
                  </Button>
                  <Button
                    onClick={() => setShowJustificationModal(false)}
                    disabled={loading}
                    variant="outline"
                    className="btn-cancel"
                  >
                    Annuler
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobOfferDetailModal;